import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { getUserAlerts, createAlert, updateAlert, toggleAlert, deleteAlert } from '../../services/propertyAlertsService';
import { communeService } from '../../services/communeService';
import { gaTrackAlertSignup } from '../../services/gaAnalyticsService';

// ─── Toggle Component ─────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-1 ${
      checked ? 'bg-brand-earth' : 'bg-border'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-ios-light ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// ─── Frequency Selector ───────────────────────────────────────────────────────

const FREQ_OPTIONS = [
  { value: 'immediate', label: 'Immédiat', icon: 'Zap' },
  { value: 'daily', label: 'Quotidien', icon: 'Sun' },
  { value: 'weekly', label: 'Hebdomadaire', icon: 'CalendarDays' },
  { value: 'never', label: 'Jamais', icon: 'BellOff' },
];

const FrequencySelector = ({ value, onChange, disabled = false }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {FREQ_OPTIONS?.map(opt => (
      <button
        key={opt?.value}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(opt?.value)}
        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-ios-caption1 font-body-medium border transition-all duration-150 ${
          value === opt?.value
            ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
            : 'bg-white text-text-secondary border-border/60 hover:border-brand-gold/50 hover:text-brand-earth'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Icon name={opt?.icon} size={12} className={value === opt?.value ? 'text-white' : ''} />
        <span>{opt?.label}</span>
      </button>
    ))}
  </div>
);

// ─── Section Card ─────────────────────────────────────────────────────────────

const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, children }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center space-x-3 px-5 py-4 border-b border-border/40">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon} size={18} className={iconColor} />
      </div>
      <div>
        <p className="text-ios-callout font-body-semibold text-text-primary">{title}</p>
        {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

// ─── Row Item ─────────────────────────────────────────────────────────────────

const SettingRow = ({ icon, iconBg, iconColor, label, description, children, divider = true }) => (
  <div className={`flex items-start justify-between gap-4 py-4 ${divider ? 'border-b border-border/30 last:border-0' : ''}`}>
    <div className="flex items-start space-x-3 flex-1 min-w-0">
      <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon name={icon} size={15} className={iconColor} />
      </div>
      <div className="min-w-0">
        <p className="text-ios-callout font-body-medium text-text-primary">{label}</p>
        {description && <p className="text-ios-caption1 text-text-secondary mt-0.5 leading-relaxed">{description}</p>}
      </div>
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

// ─── Save Button ──────────────────────────────────────────────────────────────

const SaveButton = ({ saving, saved, onClick }) => (
  <div className="flex items-center justify-end space-x-3 pt-3 mt-1 border-t border-border/30">
    {saved && (
      <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
        <Icon name="CheckCircle" size={13} />
        <span>Sauvegardé</span>
      </span>
    )}
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
    >
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Sauvegarde…</span>
        </>
      ) : (
        <>
          <Icon name="Save" size={15} color="white" />
          <span>Sauvegarder</span>
        </>
      )}
    </button>
  </div>
);

// ─── Default Prefs ────────────────────────────────────────────────────────────

const DEFAULT_PREFS = {
  // Email
  email_enabled: true,
  email_reminders: true,
  email_reminders_freq: 'immediate',
  email_promotions: true,
  email_promotions_freq: 'weekly',
  email_transactional: true,
  email_transactional_freq: 'immediate',
  // SMS
  sms_enabled: true,
  sms_reminders: true,
  sms_reminders_freq: 'immediate',
  sms_promotions: false,
  sms_promotions_freq: 'weekly',
  sms_transactional: true,
  sms_transactional_freq: 'immediate',
};

// ─── Property Alert Form Modal ────────────────────────────────────────────────

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Appartement' },
  { value: 'house', label: 'Maison' },
  { value: 'villa', label: 'Villa' },
  { value: 'land', label: 'Terrain' },
  { value: 'commercial', label: 'Commercial' },
];

const MARKET_TYPES = [
  { value: 'both', label: 'Vente & Location' },
  { value: 'sale', label: 'Vente uniquement' },
  { value: 'rental', label: 'Location uniquement' },
];

const AlertFormModal = ({ alert, communes, onSave, onClose }) => {
  const isEdit = !!alert?.id;
  const [form, setForm] = useState({
    name: alert?.name || 'Mon alerte',
    commune_ids: alert?.commune_ids || [],
    commune_names: alert?.commune_names || [],
    property_types: alert?.property_types || [],
    market_type: alert?.market_type || 'both',
    min_price: alert?.min_price || '',
    max_price: alert?.max_price || '',
    notify_email: alert?.notify_email !== false,
    notify_sms: alert?.notify_sms === true,
  });
  const [saving, setSaving] = useState(false);

  const toggleCommune = (commune) => {
    const ids = form?.commune_ids || [];
    const names = form?.commune_names || [];
    if (ids?.includes(commune?.id)) {
      setForm(f => ({
        ...f,
        commune_ids: ids?.filter(id => id !== commune?.id),
        commune_names: names?.filter(n => n !== commune?.name),
      }));
    } else {
      setForm(f => ({
        ...f,
        commune_ids: [...ids, commune?.id],
        commune_names: [...names, commune?.name],
      }));
    }
  };

  const toggleType = (type) => {
    const types = form?.property_types || [];
    if (types?.includes(type)) {
      setForm(f => ({ ...f, property_types: types?.filter(t => t !== type) }));
    } else {
      setForm(f => ({ ...f, property_types: [...types, type] }));
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      ...form,
      min_price: form?.min_price ? Number(form?.min_price) : null,
      max_price: form?.max_price ? Number(form?.max_price) : null,
    };
    await onSave(payload, isEdit ? alert?.id : null);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 pb-4 sm:pb-0">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-xl bg-brand-earth/10 flex items-center justify-center">
              <Icon name="Bell" size={16} className="text-brand-earth" />
            </div>
            <p className="text-ios-callout font-body-semibold text-text-primary">
              {isEdit ? 'Modifier l\'alerte' : 'Nouvelle alerte'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-surface flex items-center justify-center hover:bg-border/40 transition-colors">
            <Icon name="X" size={16} className="text-text-secondary" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Alert name */}
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Nom de l'alerte</label>
            <input
              type="text"
              value={form?.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ex: Appartement Cocody"
              className="w-full px-3 py-2.5 rounded-xl border border-border/60 text-ios-callout text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold/60"
            />
          </div>

          {/* Communes */}
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-2">Communes (laisser vide = toutes)</label>
            <div className="flex flex-wrap gap-2">
              {communes?.slice(0, 20)?.map(c => (
                <button
                  key={c?.id}
                  type="button"
                  onClick={() => toggleCommune(c)}
                  className={`px-3 py-1.5 rounded-xl text-ios-caption1 border transition-all duration-150 ${
                    form?.commune_ids?.includes(c?.id)
                      ? 'bg-brand-earth text-white border-brand-earth' :'bg-white text-text-secondary border-border/60 hover:border-brand-gold/50'
                  }`}
                >
                  {c?.name}
                </button>
              ))}
            </div>
          </div>

          {/* Property types */}
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-2">Types de propriété (laisser vide = tous)</label>
            <div className="flex flex-wrap gap-2">
              {PROPERTY_TYPES?.map(pt => (
                <button
                  key={pt?.value}
                  type="button"
                  onClick={() => toggleType(pt?.value)}
                  className={`px-3 py-1.5 rounded-xl text-ios-caption1 border transition-all duration-150 ${
                    form?.property_types?.includes(pt?.value)
                      ? 'bg-brand-earth text-white border-brand-earth' :'bg-white text-text-secondary border-border/60 hover:border-brand-gold/50'
                  }`}
                >
                  {pt?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Market type */}
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-2">Type de marché</label>
            <div className="flex flex-wrap gap-2">
              {MARKET_TYPES?.map(mt => (
                <button
                  key={mt?.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, market_type: mt?.value }))}
                  className={`px-3 py-1.5 rounded-xl text-ios-caption1 border transition-all duration-150 ${
                    form?.market_type === mt?.value
                      ? 'bg-brand-earth text-white border-brand-earth' :'bg-white text-text-secondary border-border/60 hover:border-brand-gold/50'
                  }`}
                >
                  {mt?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Prix min (FCFA)</label>
              <input
                type="number"
                value={form?.min_price}
                onChange={e => setForm(f => ({ ...f, min_price: e.target.value }))}
                placeholder="0"
                className="w-full px-3 py-2.5 rounded-xl border border-border/60 text-ios-callout text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold/60"
              />
            </div>
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1.5">Prix max (FCFA)</label>
              <input
                type="number"
                value={form?.max_price}
                onChange={e => setForm(f => ({ ...f, max_price: e.target.value }))}
                placeholder="Illimité"
                className="w-full px-3 py-2.5 rounded-xl border border-border/60 text-ios-callout text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold/60"
              />
            </div>
          </div>

          {/* Notification channels */}
          <div className="space-y-3">
            <label className="block text-ios-caption1 font-body-medium text-text-secondary">Canaux de notification</label>
            <div className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-surface border border-border/40">
              <div className="flex items-center space-x-2.5">
                <Icon name="Mail" size={15} className="text-blue-500" />
                <span className="text-ios-callout text-text-primary">Email</span>
              </div>
              <Toggle checked={form?.notify_email} onChange={v => setForm(f => ({ ...f, notify_email: v }))} />
            </div>
            <div className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-surface border border-border/40">
              <div className="flex items-center space-x-2.5">
                <Icon name="MessageCircle" size={15} className="text-green-500" />
                <span className="text-ios-callout text-text-primary">SMS</span>
              </div>
              <Toggle checked={form?.notify_sms} onChange={v => setForm(f => ({ ...f, notify_sms: v }))} />
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 flex items-center justify-end space-x-3 border-t border-border/30 pt-4">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border/60 text-ios-callout text-text-secondary hover:bg-surface transition-colors">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form?.name?.trim()}
            className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all shadow-ios-light"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Save" size={15} color="white" />
            )}
            <span>{isEdit ? 'Mettre à jour' : 'Créer l\'alerte'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Alert Card ───────────────────────────────────────────────────────────────

const AlertCard = ({ alert, onToggle, onEdit, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(alert?.id);
    setDeleting(false);
  };

  const communeLabel = alert?.commune_names?.length > 0
    ? alert?.commune_names?.slice(0, 3)?.join(', ') + (alert?.commune_names?.length > 3 ? ` +${alert?.commune_names?.length - 3}` : '')
    : 'Toutes les communes';

  const typeLabel = alert?.property_types?.length > 0
    ? alert?.property_types?.join(', ')
    : 'Tous les types';

  const marketLabel = alert?.market_type === 'sale' ? 'Vente' : alert?.market_type === 'rental' ? 'Location' : 'Vente & Location';

  const priceLabel = alert?.min_price || alert?.max_price
    ? [
        alert?.min_price ? `${new Intl.NumberFormat('fr-FR').format(alert?.min_price)} FCFA` : null,
        alert?.max_price ? `${new Intl.NumberFormat('fr-FR').format(alert?.max_price)} FCFA` : null,
      ].filter(Boolean).join(' – ')
    : 'Tous les prix';

  return (
    <div className={`rounded-2xl border p-4 transition-all duration-200 ${alert?.is_active ? 'bg-white border-border/50 shadow-ios-light' : 'bg-surface/50 border-border/30 opacity-70'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center space-x-2.5 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${alert?.is_active ? 'bg-brand-earth/10' : 'bg-border/30'}`}>
            <Icon name="Bell" size={15} className={alert?.is_active ? 'text-brand-earth' : 'text-text-secondary'} />
          </div>
          <p className="text-ios-callout font-body-semibold text-text-primary truncate">{alert?.name}</p>
        </div>
        <div className="flex items-center space-x-1.5 flex-shrink-0">
          <button
            onClick={() => onEdit(alert)}
            className="w-7 h-7 rounded-lg bg-surface hover:bg-border/40 flex items-center justify-center transition-colors"
            title="Modifier"
          >
            <Icon name="Pencil" size={13} className="text-text-secondary" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
            title="Supprimer"
          >
            {deleting ? (
              <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Trash2" size={13} className="text-red-500" />
            )}
          </button>
          <Toggle checked={alert?.is_active} onChange={v => onToggle(alert?.id, v)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[
          { icon: 'MapPin', label: communeLabel },
          { icon: 'Home', label: typeLabel },
          { icon: 'Tag', label: marketLabel },
          { icon: 'Banknote', label: priceLabel },
        ]?.map((item, i) => (
          <div key={i} className="flex items-center space-x-1.5">
            <Icon name={item?.icon} size={12} className="text-text-secondary flex-shrink-0" />
            <span className="text-ios-caption2 text-text-secondary truncate">{item?.label}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center space-x-3 mt-3 pt-3 border-t border-border/30">
        {alert?.notify_email && (
          <div className="flex items-center space-x-1">
            <Icon name="Mail" size={12} className="text-blue-500" />
            <span className="text-ios-caption2 text-blue-600">Email</span>
          </div>
        )}
        {alert?.notify_sms && (
          <div className="flex items-center space-x-1">
            <Icon name="MessageCircle" size={12} className="text-green-500" />
            <span className="text-ios-caption2 text-green-600">SMS</span>
          </div>
        )}
        {alert?.last_notified_at && (
          <span className="text-ios-caption2 text-text-secondary ml-auto">
            Dernière alerte: {new Date(alert?.last_notified_at)?.toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const CommunicationSettings = () => {
  const { user } = useAuth();

  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // ─── Property Alerts state ─────────────────────────────────────────────
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [communes, setCommunes] = useState([]);

  const loadPrefs = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        ?.from('communication_settings')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (!fetchErr && data) {
        setPrefs({
          email_enabled: data?.email_enabled ?? true,
          email_reminders: data?.email_reminders ?? true,
          email_reminders_freq: data?.email_reminders_freq ?? 'immediate',
          email_promotions: data?.email_promotions ?? true,
          email_promotions_freq: data?.email_promotions_freq ?? 'weekly',
          email_transactional: data?.email_transactional ?? true,
          email_transactional_freq: data?.email_transactional_freq ?? 'immediate',
          sms_enabled: data?.sms_enabled ?? true,
          sms_reminders: data?.sms_reminders ?? true,
          sms_reminders_freq: data?.sms_reminders_freq ?? 'immediate',
          sms_promotions: data?.sms_promotions ?? false,
          sms_promotions_freq: data?.sms_promotions_freq ?? 'weekly',
          sms_transactional: data?.sms_transactional ?? true,
          sms_transactional_freq: data?.sms_transactional_freq ?? 'immediate',
        });
      }
    } catch (err) {
      console.error('Error loading communication settings:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const loadAlerts = useCallback(async () => {
    if (!user?.id) return;
    setAlertsLoading(true);
    const { data } = await getUserAlerts(user?.id);
    setAlerts(data || []);
    setAlertsLoading(false);
  }, [user?.id]);

  const loadCommunes = useCallback(async () => {
    try {
      const result = await communeService?.getAllCommunes?.();
      if (result?.data) setCommunes(result?.data);
      else {
        const { data } = await supabase?.from('communes')?.select('id, name')?.order('name');
        setCommunes(data || []);
      }
    } catch {
      const { data } = await supabase?.from('communes')?.select('id, name')?.order('name');
      setCommunes(data || []);
    }
  }, []);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);
  useEffect(() => { if (user?.id) { loadAlerts(); loadCommunes(); } }, [user?.id, loadAlerts, loadCommunes]);

  const set = (key, val) => setPrefs(p => ({ ...p, [key]: val }));

  const handleSaveAlert = async (formData, alertId) => {
    if (alertId) {
      const { data } = await updateAlert(alertId, formData);
      if (data) setAlerts(prev => prev?.map(a => a?.id === alertId ? data : a));
    } else {
      const { data } = await createAlert(user?.id, formData);
      if (data) {
        setAlerts(prev => [data, ...prev]);
        // Track alert creation in GA — Funnel Stage 2
        gaTrackAlertSignup(formData);
      }
    }
    setShowAlertModal(false);
    setEditingAlert(null);
  };

  const handleToggleAlert = async (alertId, isActive) => {
    const { data } = await toggleAlert(alertId, isActive);
    if (data) setAlerts(prev => prev?.map(a => a?.id === alertId ? data : a));
  };

  const handleDeleteAlert = async (alertId) => {
    const { error: delErr } = await deleteAlert(alertId);
    if (!delErr) setAlerts(prev => prev?.filter(a => a?.id !== alertId));
  };

  const handleEditAlert = (alert) => {
    setEditingAlert(alert);
    setShowAlertModal(true);
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setError('');
    setSaving(true);
    try {
      const { error: upsertErr } = await supabase
        ?.from('communication_settings')
        ?.upsert({
          user_id: user?.id,
          ...prefs,
          updated_at: new Date()?.toISOString(),
        }, { onConflict: 'user_id' });

      if (upsertErr) {
        // Table may not exist yet — store locally as fallback
        localStorage.setItem(`comm_settings_${user?.id}`, JSON.stringify(prefs));
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        return;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Une erreur est survenue lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-surface/30">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mx-auto mb-5">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-3">Connexion requise</h2>
          <p className="text-ios-callout text-text-secondary mb-6">Connectez-vous pour gérer vos paramètres de communication.</p>
          <Link to="/homepage" className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors">
            <Icon name="Home" size={16} color="white" />
            <span>Retour à l'accueil</span>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Paramètres de Communication – Kapital States</title>
        <meta name="description" content="Contrôlez la fréquence de vos emails et SMS : rappels, promotions et messages transactionnels." />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-6 flex-wrap gap-y-1">
            <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
            <Icon name="ChevronRight" size={12} />
            <Link to="/account-dashboard" className="hover:text-brand-earth transition-colors">Mon Compte</Link>
            <Icon name="ChevronRight" size={12} />
            <span className="text-text-primary font-body-medium">Paramètres de communication</span>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-brand-earth flex items-center justify-center shadow-ios-light flex-shrink-0">
                <Icon name="MessageSquare" size={26} color="white" />
              </div>
              <div>
                <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-tight">
                  Paramètres de communication
                </h1>
                <p className="text-ios-callout text-text-secondary mt-0.5">
                  Contrôlez vos emails et SMS par catégorie
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">

              {/* ── Master toggles ── */}
              <SectionCard icon="ToggleRight" iconBg="bg-slate-100" iconColor="text-slate-600" title="Canaux de communication" subtitle="Activez ou désactivez un canal entier">
                <div>
                  <SettingRow
                    icon="Mail"
                    iconBg="bg-blue-50"
                    iconColor="text-blue-600"
                    label="Emails"
                    description="Recevoir des communications par email"
                  >
                    <Toggle checked={prefs?.email_enabled} onChange={v => set('email_enabled', v)} />
                  </SettingRow>
                  <SettingRow
                    icon="MessageCircle"
                    iconBg="bg-green-50"
                    iconColor="text-green-600"
                    label="SMS"
                    description="Recevoir des communications par SMS"
                    divider={false}
                  >
                    <Toggle checked={prefs?.sms_enabled} onChange={v => set('sms_enabled', v)} />
                  </SettingRow>
                </div>
              </SectionCard>

              {/* ── Email settings ── */}
              <SectionCard
                icon="Mail"
                iconBg="bg-blue-50"
                iconColor="text-blue-600"
                title="Paramètres Email"
                subtitle="Fréquence et opt-in par catégorie"
              >
                <div className={`transition-opacity duration-200 ${!prefs?.email_enabled ? 'opacity-40 pointer-events-none' : ''}`}>

                  {/* Reminders */}
                  <div className="py-4 border-b border-border/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Bell" size={15} className="text-amber-600" />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">Rappels</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">Rappels de rendez-vous, visites et échéances</p>
                          {prefs?.email_reminders && (
                            <FrequencySelector
                              value={prefs?.email_reminders_freq}
                              onChange={v => set('email_reminders_freq', v)}
                              disabled={!prefs?.email_enabled}
                            />
                          )}
                        </div>
                      </div>
                      <Toggle checked={prefs?.email_reminders} onChange={v => set('email_reminders', v)} disabled={!prefs?.email_enabled} />
                    </div>
                  </div>

                  {/* Promotions */}
                  <div className="py-4 border-b border-border/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Tag" size={15} className="text-rose-500" />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">Promotions</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">Offres spéciales, nouvelles propriétés et actualités du marché</p>
                          {prefs?.email_promotions && (
                            <FrequencySelector
                              value={prefs?.email_promotions_freq}
                              onChange={v => set('email_promotions_freq', v)}
                              disabled={!prefs?.email_enabled}
                            />
                          )}
                        </div>
                      </div>
                      <Toggle checked={prefs?.email_promotions} onChange={v => set('email_promotions', v)} disabled={!prefs?.email_enabled} />
                    </div>
                  </div>

                  {/* Transactional */}
                  <div className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Receipt" size={15} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">Messages transactionnels</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">Confirmations de réservation, reçus et mises à jour de statut</p>
                          {prefs?.email_transactional && (
                            <FrequencySelector
                              value={prefs?.email_transactional_freq}
                              onChange={v => set('email_transactional_freq', v)}
                              disabled={!prefs?.email_enabled}
                            />
                          )}
                        </div>
                      </div>
                      <Toggle checked={prefs?.email_transactional} onChange={v => set('email_transactional', v)} disabled={!prefs?.email_enabled} />
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* ── SMS settings ── */}
              <SectionCard
                icon="MessageCircle"
                iconBg="bg-green-50"
                iconColor="text-green-600"
                title="Paramètres SMS"
                subtitle="Fréquence et opt-in par catégorie"
              >
                <div className={`transition-opacity duration-200 ${!prefs?.sms_enabled ? 'opacity-40 pointer-events-none' : ''}`}>

                  {/* Reminders */}
                  <div className="py-4 border-b border-border/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Bell" size={15} className="text-amber-600" />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">Rappels</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">Rappels de rendez-vous et visites par SMS</p>
                          {prefs?.sms_reminders && (
                            <FrequencySelector
                              value={prefs?.sms_reminders_freq}
                              onChange={v => set('sms_reminders_freq', v)}
                              disabled={!prefs?.sms_enabled}
                            />
                          )}
                        </div>
                      </div>
                      <Toggle checked={prefs?.sms_reminders} onChange={v => set('sms_reminders', v)} disabled={!prefs?.sms_enabled} />
                    </div>
                  </div>

                  {/* Promotions */}
                  <div className="py-4 border-b border-border/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Tag" size={15} className="text-rose-500" />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">Promotions</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">Offres et alertes de nouvelles propriétés par SMS</p>
                          {prefs?.sms_promotions && (
                            <FrequencySelector
                              value={prefs?.sms_promotions_freq}
                              onChange={v => set('sms_promotions_freq', v)}
                              disabled={!prefs?.sms_enabled}
                            />
                          )}
                        </div>
                      </div>
                      <Toggle checked={prefs?.sms_promotions} onChange={v => set('sms_promotions', v)} disabled={!prefs?.sms_enabled} />
                    </div>
                  </div>

                  {/* Transactional */}
                  <div className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Receipt" size={15} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">Messages transactionnels</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">Confirmations de réservation et codes de vérification</p>
                          {prefs?.sms_transactional && (
                            <FrequencySelector
                              value={prefs?.sms_transactional_freq}
                              onChange={v => set('sms_transactional_freq', v)}
                              disabled={!prefs?.sms_enabled}
                            />
                          )}
                        </div>
                      </div>
                      <Toggle checked={prefs?.sms_transactional} onChange={v => set('sms_transactional', v)} disabled={!prefs?.sms_enabled} />
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* ── Property Alerts ── */}
              <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="BellRing" size={18} className="text-brand-gold" />
                    </div>
                    <div>
                      <p className="text-ios-callout font-body-semibold text-text-primary">Alertes propriétés</p>
                      <p className="text-ios-caption1 text-text-secondary mt-0.5">Soyez notifié dès qu'une propriété correspond à vos critères</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setEditingAlert(null); setShowAlertModal(true); }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-brand-earth text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-brand-earth/90 transition-colors shadow-ios-light"
                  >
                    <Icon name="Plus" size={13} color="white" />
                    <span>Nouvelle alerte</span>
                  </button>
                </div>

                <div className="px-5 py-5">
                  {alertsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : alerts?.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center mx-auto mb-3">
                        <Icon name="BellOff" size={22} className="text-brand-gold/60" />
                      </div>
                      <p className="text-ios-callout font-body-medium text-text-primary mb-1">Aucune alerte configurée</p>
                      <p className="text-ios-caption1 text-text-secondary mb-4">Créez une alerte pour être notifié des nouvelles propriétés correspondant à vos critères.</p>
                      <button
                        onClick={() => { setEditingAlert(null); setShowAlertModal(true); }}
                        className="inline-flex items-center space-x-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors"
                      >
                        <Icon name="Plus" size={15} color="white" />
                        <span>Créer ma première alerte</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {alerts?.map(alert => (
                        <AlertCard
                          key={alert?.id}
                          alert={alert}
                          onToggle={handleToggleAlert}
                          onEdit={handleEditAlert}
                          onDelete={handleDeleteAlert}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Error */}
              {error && (
                <p className="text-ios-caption1 text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-100">{error}</p>
              )}

              {/* Save */}
              <SaveButton saving={saving} saved={saved} onClick={handleSave} />

              {/* Info note */}
              <div className="flex items-start space-x-3 p-4 rounded-2xl bg-blue-50 border border-blue-100">
                <Icon name="Info" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-ios-caption1 text-blue-700 leading-relaxed">
                  Les messages transactionnels essentiels (confirmations de paiement, codes de sécurité) peuvent être envoyés même si vous les désactivez, conformément à nos obligations légales.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Alert Form Modal */}
      {showAlertModal && (
        <AlertFormModal
          alert={editingAlert}
          communes={communes}
          onSave={handleSaveAlert}
          onClose={() => { setShowAlertModal(false); setEditingAlert(null); }}
        />
      )}
    </>
  );
};

export default CommunicationSettings;
